/**
 * FollowUpQuestionsSection v173.0.0 — Inline Follow-Up Questions
 * 
 * Renders AI-generated follow-up questions inline in the DeliverScreen
 * after the voice/text gate clears. Questions cover symptoms, medication
 * adherence, care order completion, and side effects in one unified flow.
 * 
 * Uses the same card/button pattern as DescribeAdaptive + DescribeSection
 * for seamless visual continuity.
 * 
 * Props:
 *   questions     — array of { id, text, type, options, category, context }
 *   loading       — boolean, show generating spinner
 *   error         — string, show error state
 *   onComplete    — (answers) => void, called when user finishes
 *   onRetry       — () => void, retry AI generation
 *   isLocked      — boolean, read-only mode
 */

import { useState, useEffect, useRef } from 'react'
import { Circle, RefreshCw, AlertCircle } from 'lucide-react'
import { useI18n } from '../../i18n'
import { OrellaSpinner } from '../../components/OrellaLoader'

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const ICE_BLUE = '#E9F8FF'

/**
 * Standard fallback follow-up check-in questions.
 *
 * v217.1.5: When AI question generation fails, the client substitutes
 * this generic set so the user can still report their status. Categories
 * align with categorizeFollowUpAnswers() so the downstream payload shape
 * is identical to AI-generated questions.
 *
 * Keep this set short (≤5) and broadly applicable across specialties.
 * No specialty-specific terminology, no doctor names.
 */
export const STANDARD_FOLLOWUP_QUESTIONS = [
  {
    id: 'std_overall',
    text: 'How are you feeling overall since your last visit?',
    type: 'single-select',
    options: ['Much better', 'Somewhat better', 'About the same', 'Somewhat worse', 'Much worse'],
    category: 'symptom',
  },
  {
    id: 'std_meds',
    text: 'Are you taking your medications as prescribed?',
    type: 'single-select',
    options: ['Yes, as prescribed', 'Mostly', 'Sometimes missing doses', 'Often missing doses', 'Stopped taking them'],
    category: 'medication',
  },
  {
    id: 'std_side_effects',
    text: 'Have you experienced any side effects from your medications?',
    type: 'single-select',
    options: ['None', 'Mild', 'Moderate', 'Severe'],
    category: 'side_effect',
  },
  {
    id: 'std_care_orders',
    text: 'Have you completed the tests, imaging, or referrals your doctor ordered?',
    type: 'single-select',
    options: ['All completed', 'Some completed', 'Scheduled but not done yet', "Haven't started"],
    category: 'care_order',
  },
  {
    id: 'std_open',
    text: "Anything else you'd like your doctor to know?",
    type: 'free-text',
    category: 'open',
  },
]

// Replace specific doctor names with "your doctor" in AI-generated questions
const sanitizeDoctorName = (text) => {
  if (!text) return text
  return text
    .replace(/\[Doctor\s*Name\]/gi, 'your doctor')
    .replace(/Dr\.\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*/g, 'your doctor')
}

export default function FollowUpQuestionsSection({ 
  questions, 
  loading, 
  error, 
  onComplete, 
  onRetry, 
  isLocked = false,
  initialAnswers = {},
  onAnswersChange
}) {
  const { t } = useI18n()
  const ts = (key, fallback) => { const v = t(key); return (!v || v === key) ? fallback : v }
  const [answers, setAnswers] = useState(initialAnswers)
  const [details, setDetails] = useState({})
  const scrollRef = useRef(null)

  // Restore answers from initial if they arrive later
  useEffect(() => {
    if (initialAnswers && Object.keys(initialAnswers).length > 0 && Object.keys(answers).length === 0) {
      setAnswers(initialAnswers)
    }
  }, [initialAnswers])

  // Notify parent of answer changes
  useEffect(() => {
    onAnswersChange?.(answers, details)
  }, [answers, details])

  const handleSingleSelect = (questionId, option) => {
    if (isLocked) return
    setAnswers(prev => ({
      ...prev,
      [questionId]: prev[questionId] === option ? '' : option
    }))
  }

  const handleFreeText = (questionId, value) => {
    if (isLocked) return
    setAnswers(prev => ({ ...prev, [questionId]: value }))
  }

  const handleDetailText = (questionId, value) => {
    if (isLocked) return
    setDetails(prev => ({ ...prev, [questionId]: value }))
  }

  const answeredCount = questions ? questions.filter(q => {
    const a = answers[q.id]
    return a && (typeof a === 'string' ? a.trim().length > 0 : true)
  }).length : 0

  const totalCount = questions?.length || 0

  // Loading state
  if (loading) {
    return (
      <div className="mx-4 my-3 rounded-2xl border-[1.5px] border-dashed border-[#A7D9FC] bg-[#F0F9FF] p-6 text-center">
        {/* ★★ D332(q): v237.0.0 called this "canonical" IN A COMMENT while being the third
            hand-copy of the ring. The canonical thing is now a component. */}
        <OrellaSpinner size={48} className="mx-auto mb-3" />
        <p className="text-[13px] font-semibold text-[#15354F]">
          {ts('deliver.preparingQuestions', 'Preparing your follow-up questions...')}
        </p>
      </div>
    )
  }

  // v217.1.5: Hard error state — only shown when AI failed AND no fallback
  // questions were substituted. With the fallback wired in FollowUpReportModal,
  // we should always have questions to show; this stays as a defensive branch.
  if (error && (!questions || questions.length === 0)) {
    return (
      <div className="mx-4 my-3 rounded-2xl border-[1.5px] border-amber-200 bg-amber-50 p-5">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-[13px] font-semibold text-amber-800">
              {ts('deliver.questionsFailed', "Couldn't generate personalized questions")}
            </p>
            <p className="text-[11px] text-amber-700 mt-1">
              {ts('deliver.usingStandardCheckin', "We'll use standard check-in questions instead.")}
            </p>
          </div>
        </div>
        {onRetry && (
          <button
            onClick={onRetry}
            className="mt-3 flex items-center gap-1.5 text-[12px] font-semibold text-amber-700 bg-transparent border-none cursor-pointer"
          >
            <RefreshCw size={12} /> {ts('common.tryAgain', 'Try again')}
          </button>
        )}
      </div>
    )
  }

  // No questions
  if (!questions || questions.length === 0) return null

  // v217.1.5: When error + questions BOTH present, the parent substituted the
  // standard fallback set after AI generation failed. Render a soft amber
  // banner above the questions explaining what happened, then the questions.
  const showFallbackBanner = !!error && questions.length > 0

  return (
    <div className="mx-4 my-3">
      {/* v217.1.5: Soft fallback banner — only shown when AI question
          generation failed and the parent substituted the standard set. */}
      {showFallbackBanner && (
        <div className="mb-3 rounded-2xl border-[1.5px] border-amber-200 bg-amber-50 p-3.5">
          <div className="flex items-start gap-2.5">
            <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-[12px] font-semibold text-amber-800">
                {ts('deliver.usingStandardCheckin', "We'll use standard check-in questions instead.")}
              </p>
              {onRetry && (
                <button
                  onClick={onRetry}
                  className="mt-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-amber-700 bg-transparent border-none cursor-pointer"
                >
                  <RefreshCw size={11} /> {ts('common.tryAgain', 'Try again')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Questions card */}
      <div className="rounded-2xl border-[1.5px] border-gray-200 bg-white overflow-hidden">
        {/* Questions */}
        <div ref={scrollRef}>
          {questions.map((question, qIndex) => (
            <div key={question.id} className="px-4 py-4 border-b border-gray-100 last:border-b-0">
              {/* Question text */}
              <p className="font-medium text-gray-800 text-[13px] mb-0.5">
                <span className="text-[#46A8F9] font-bold">{qIndex + 1}.</span> {sanitizeDoctorName(question.text)}
              </p>

              {/* Context line */}
              {question.context && (
                <p className="text-[10.5px] text-gray-400 italic mt-1 flex items-center gap-1">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
                  </svg>
                  {question.context}
                </p>
              )}

              {/* Answer options */}
              <div className="mt-2">
                {question.type === 'free-text' ? (
                  <textarea
                    value={answers[question.id] || ''}
                    onChange={(e) => handleFreeText(question.id, e.target.value)}
                    disabled={isLocked}
                    placeholder={ts('deliver.typeResponse', 'Type your response here...')}
                    className="w-full min-h-[80px] rounded-xl border-[1.5px] border-gray-200 px-3.5 py-3 text-[13px] text-gray-700 placeholder-gray-300 resize-y outline-none focus:border-[#46A8F9] transition-colors"
                    style={{ fontFamily: 'inherit' }}
                  />
                ) : (
                  (question.options || []).map((option) => {
                    const isSelected = answers[question.id] === option
                    return (
                      <button
                        key={option}
                        onClick={() => handleSingleSelect(question.id, option)}
                        disabled={isLocked}
                        className={`w-full p-2.5 rounded-xl border-[1.5px] text-left flex items-center gap-2.5 transition-all mt-2 first:mt-0 ${
                          isSelected
                            ? 'border-[#46A8F9] bg-[#E9F8FF]'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        style={{ cursor: isLocked ? 'default' : 'pointer' }}
                      >
                        <div className={`w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'border-[#46A8F9]' : 'border-gray-300'
                        }`}>
                          {isSelected && <Circle size={8} className="text-[#46A8F9] fill-[#46A8F9]" />}
                        </div>
                        <span className={`text-[13px] ${isSelected ? 'text-[#15354F] font-semibold' : 'text-gray-600'}`}>
                          {option}
                        </span>
                      </button>
                    )
                  })
                )}

                {/* Detail textbox — show for non-free-text questions that might need elaboration */}
                {question.type !== 'free-text' && answers[question.id] && (
                  <textarea
                    value={details[question.id] || ''}
                    onChange={(e) => handleDetailText(question.id, e.target.value)}
                    disabled={isLocked}
                    placeholder={ts('deliver.addDetails', 'Add any details (optional)...')}
                    className="w-full min-h-[50px] mt-2 rounded-xl border-[1.5px] border-gray-200 px-3 py-2.5 text-[12px] text-gray-600 placeholder-gray-300 resize-none outline-none focus:border-[#46A8F9] transition-colors"
                    style={{ fontFamily: 'inherit' }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-3 mb-1">
        <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#66D170] rounded-full transition-all duration-400"
            style={{ width: `${totalCount > 0 ? (answeredCount / totalCount) * 100 : 0}%` }}
          />
        </div>
        <p className="text-[11px] font-semibold text-gray-400 mt-1.5 text-right">
          {answeredCount} of {totalCount} answered
        </p>
      </div>
    </div>
  )
}

// Helper: extract categorized answers from the questions + answers state
// Used by DeliverScreen to build the pre_visit_report payload
export function categorizeFollowUpAnswers(questions, answers, details) {
  if (!questions || !answers) return { symptom: {}, medication: {}, care_order: {}, side_effect: {}, open: {} }
  
  const categorized = { symptom: {}, medication: {}, care_order: {}, side_effect: {}, open: {} }
  
  for (const q of questions) {
    const answer = answers[q.id]
    if (!answer) continue
    const cat = q.category || 'symptom'
    const bucket = categorized[cat] || categorized.symptom
    bucket[q.id] = {
      question: q.text,
      answer: answer,
      detail: details?.[q.id] || '',
      type: q.type,
      clinicalRationale: q.clinicalRationale || '',
    }
  }
  
  return categorized
}
